import {useMutation} from '@apollo/client'
import React, {useEffect} from 'react'
import {Alert, Button, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {DELETE_PIPELINE} from '../../../gql/mutations/pipelineMutations'
import {IResDeletePipe} from '../../../types/pipelines'
import {toast} from 'react-toastify'
import {parseId} from '../../../helpers'

interface IModalDeletePipeline {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  infoEdit: {id: string; name: string}
}

const quote = '"'

const dpM = defineMessages({
  important: {id: 'leads.pipelines.important', defaultMessage: 'Important'},
  notDeletedIfLeads: {id: 'leads.pipelines.notDeletedIfLeads', defaultMessage: 'This pipeline will not be deleted if it has associated Leads.'},
  confirm: {id: 'leads.pipelines.deleteConfirm', defaultMessage: 'Are you sure you want to delete this pipeline <b>{name}</b>?'},
  toastDeleted: {id: 'leads.pipelines.toast.deleted', defaultMessage: 'Pipeline deleted successfully'},
})

export const ModalDeletePipeline: React.FC<IModalDeletePipeline> = ({
  infoEdit,
  isOpen,
  onClose,
  refetch,
}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})

  const [gqlAddEditPipeline, {data, loading, error}] = useMutation<IResDeletePipe>(DELETE_PIPELINE)

  useEffect(() => {
    if (error) toast.error(errMsg(error.message))
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.deletePipeline?.success) {
      toast.success(intl.formatMessage(dpM.toastDeleted))
      onClose()
      refetch()
    } else if (data.deletePipeline?.errors?.length > 0) {
      toast.error(errMsg(data.deletePipeline?.errors?.[0]?.message || intl.formatMessage({id: 'leads.add.toast.somethingHappened', defaultMessage: 'something happened'})))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleDelete = () => {
    gqlAddEditPipeline({
      variables: {
        pipelineId: parseId(infoEdit.id),
      },
    })
  }
  return (
    <Modal show={isOpen} onHide={onClose}>
      <Modal.Header closeButton>
        <Modal.Title><FormattedMessage id='leads.pipelines.deleteTooltip' defaultMessage='Delete Pipeline' /></Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant='warning' className='my-4 '>
          <Alert.Heading className='d-flex align-items-center gap-2'>
            <i className='bi bi-exclamation-circle fs-3' style={{color: 'inherit'}} />
            {intl.formatMessage(dpM.important)}
          </Alert.Heading>
          <p>{intl.formatMessage(dpM.notDeletedIfLeads)}</p>
        </Alert>
        <h2 className='text-danger'>
          {intl.formatMessage(dpM.confirm, {name: infoEdit.name, b: (chunks: (string | JSX.Element)[]) => <span className='text-primary'>{quote}{chunks}{quote}</span>})}
        </h2>
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
        </Button>
        <Button
          variant='danger'
          className='d-flex align-items-center'
          disabled={loading}
          onClick={handleDelete}
        >
          {loading && <Spinner animation='border' size='sm' />}
          <FormattedMessage id='common.action.delete' defaultMessage='Delete' />
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
